/**
 * Kontraktens feilform `{ code, message }` som JavaScript-feil. Klienten forgrener
 * på `code`, aldri på `message` (common.tsp) — og VISER aldri `message`: serverens
 * setninger er skrevet for utvikleren som integrerer, med feltnavn og K-koder, og
 * panelet eier sine egne (DD-50; UX-gjennomgangen 2026-09-14, B6). `brukertekst` er
 * det panelet viser; `message` står igjen for logger og tester.
 */
export class ApiFeil extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    melding: string,
  ) {
    super(melding);
    this.name = 'ApiFeil';
  }

  static fra(status: number, body: unknown): ApiFeil {
    const b = (body ?? {}) as { code?: unknown; message?: unknown };
    const code = typeof b.code === 'string' ? b.code : status === 401 ? 'unauthenticated' : 'unknown';
    const message = typeof b.message === 'string' ? b.message : `Uventet svar (${status}).`;
    return new ApiFeil(status, code, message);
  }

  /** Setningen panelet viser — fra koden, aldri fra serverens `message`. */
  get brukertekst(): string {
    return brukertekstFor(this.code, this.status);
  }
}

export const erApiFeil = (e: unknown): e is ApiFeil => e instanceof ApiFeil;

/**
 * Kallet nådde aldri fram: den genererte klienten gir ingen `response` når `fetch` kaster, og
 * `ApiFeil.fra` får status 0. Det er uten nett, ikke en feil fra tjeneren — teksten står.
 */
export const erNettfeil = (e: unknown): boolean => erApiFeil(e) && e.status === 0;

/** Kontraktens ErrorCode (common.tsp) → panelets setning. Ukjent kode får en generisk. */
const TEKSTER: Record<string, string> = {
  validation_failed: 'Et felt har en verdi som ikke er gyldig. Sjekk det du skrev og prøv igjen.',
  unknown_field: 'Forespørselen hadde et felt som ikke finnes. Last siden på nytt og prøv igjen.',
  text_too_short: 'Teksten er for kort.',
  text_too_long: 'Teksten er for lang.',
  correlation_invalid: 'Koblingsnøkkelen har feil form.',
  unauthenticated: 'Økten er over. Logg inn på nytt.',
  mfa_required: 'Panelet krever tofaktor. Bekreft med autentiseringsappen.',
  insufficient_role: 'Rollen din kan ikke gjøre dette.',
  insufficient_scope: 'Nøkkelen har ikke tilgang til dette.',
  screenshots_disabled: 'Skjermbilder er skrudd av for denne leietakeren.',
  invalid_status_transition: 'Statusen kan ikke settes herfra. Last saken på nytt — noen andre kan ha endret den.',
  reply_exists: 'Saken har allerede fått svar.',
  // DD-60 presisert 2026-09-25: «sete» går ut av brukertekstene. Aldri HVOR adressen er i bruk (K2).
  admin_user_email_taken: 'Adressen er alt i bruk.',
  // H2.7: aldri HVILKEN organisasjon som har domenet (K2).
  seat_domain_taken: 'Domenet er alt registrert hos en annen organisasjon.',
  cannot_change_own_role: 'Du kan ikke endre din egen tilgang. En annen eier må gjøre det.',
  // M4.8: nås fra panelet bare i kappløp — en annen eier gjorde personen til eier imens.
  owner_has_all_rooms: 'Personen er eier og har alt i alle rom. Hent siden på nytt.',
  room_not_enabled: 'Rommet er ikke skrudd på hos dere.',
  reply_required: 'Skriv en begrunnelse til innsenderen.',
  reason_code_required: 'Velg en grunn for avslaget.',
  template_unmodified: 'Skriv om malen før du sender avslaget.',
  fixed_in_required: 'Oppgi versjonen den er rettet i.',
  canonical_is_duplicate: 'Saken du valgte er selv slått sammen med en annen. Velg den saken i stedet.',
  known_issue_conflict: 'Saken er allerede knyttet til en kjent feil.',
  deadline_cannot_be_extended: 'Fristen kan kortes, aldri forlenges.',
  minors_present_conflict: 'Innstillingen er låst fordi brukerne kan være mindreårige.',
  contacts_exceed_tenant_cap: 'Programmet kan ikke ha flere kontakter enn leietakerens tak.',
  program_incomplete: 'Programmet mangler hypotese, suksesskriterium eller en oppgave med tid.',
  program_full: 'Programmet er fullt.',
  invitation_invalid: 'Invitasjonen er ugyldig eller utløpt.',
  invitation_already_answered: 'Invitasjonen er allerede besvart.',
  task_already_completed: 'Oppgaven er allerede fullført.',
  follow_up_already_answered: 'Oppfølgingen er allerede besvart.',
  follow_up_not_answerable: 'Oppfølgingen kan ikke besvares nå.',
  report_frozen: 'Rapporten er fryst og kan ikke endres.',
  data_limitations_required: 'Under terskelen må «Begrensninger i data» fylles ut.',
  webhook_not_verified: 'Verifiser webhooken før den kan skrus på.',
  cannot_disable_self: 'Du kan ikke deaktivere din egen konto. En annen eier må gjøre det.',
  attachment_already_uploaded: 'Bildet er allerede lastet opp.',
  attachment_too_large: 'Bildet er for stort.',
  attachment_type_not_allowed: 'Bildetypen støttes ikke.',
  handoff_invalid: 'Overleveringen er ugyldig eller utløpt.',
  rate_limited: 'For mange forsøk på kort tid. Vent litt og prøv igjen.',
  not_implemented: 'Dette finnes ikke ennå.',
  // Rommet Hypoteser (H2.2). Skjemaet viser låsen FØR kallet; disse er for kappløpet.
  hypothesis_incomplete: 'Hypotesen mangler noe før den kan sendes inn.',
  display_name_missing: 'Sett visningsnavnet ditt før du sender inn.',
  draft_changed_elsewhere: 'Utkastet er lagret et annet sted. Velg hvilken versjon som skal gjelde.',
  wording_locked: 'Hypotesen kan ikke endres lenger.',
  // Konklusjonen (W11): skjemaet viser låsene FØR kallet; disse er for kappløpet.
  conclusion_frozen: 'Konklusjonen er bekreftet og kan ikke endres. En rettelse er et funn.',
  open_blocking_required: '«Ingen» krever en setning om hvorfor.',
  threshold_not_numeric: 'Terskelen må være et tall.',
  not_submitter: 'Bare den som sendte inn hypotesen, kan gjøre dette.',
  // «Start testen» (H2.2, W10): portene i kontraktens rekkefølge. Oppstartskortet viser låsen FØR
  // kallet; disse er for kappløpet. `hypothesis_incomplete` har egen setning der (start, ikke send inn).
  owner_not_accepted: 'Testen kan ikke starte før eieren har tatt eierskapet.',
  revision_unapproved: 'Justeringen er ikke avgjort ennå. Testen starter på en godkjent versjon.',
  measurement_not_ready: 'Kilden må bygges før testen kan starte. Den låsen kan ingen løsne.',
  baseline_missing: 'Baseline er ikke målt. Beslutningstakeren kan løsne låsen.',
  source_unverified: 'Kilden er ikke verifisert. Beslutningstakeren kan løsne låsen.',
  measurement_points_insufficient: 'Perioden gir under to målepunkter. Forleng perioden, eller start likevel.',
  not_proposed_owner: 'Du er ikke foreslått som eier av denne lenger.',
  // H3.1: grunnlaget og notisen.
  conclusion_notice_already_sent: 'Notisen er alt avgjort — sendt, eller valgt bort. Den kan ikke sendes på nytt.',
  conclusion_notice_no_recipients: 'Ingen av sakene i grunnlaget kan få en notis, så det finnes ingen å sende til.',
  evidence_locked: 'Grunnlaget er låst fordi notisen er avgjort. Saker kan ikke legges til eller fjernes.',
  // H3.5: lenkene. Skjemaet viser låsen og feltfeilen FØR kallet; disse er for kappløpet.
  external_link_limit_reached: 'Hypotesen har alt tre lenker. Fjern én for å legge til en ny.',
  external_link_url_invalid: 'Adressen må begynne med https:// og kan ikke ha brukernavn eller passord i seg.',
  external_link_not_found: 'Lenken finnes ikke lenger. Noen andre kan ha fjernet den.',

  // H3.2: møtet. Sidene viser låsene FØR kallet; disse er for kappløpet.
  not_meeting_registrar: 'Bare den som registrerer møtet, kan gjøre dette.',
  meeting_not_found: 'Fant ikke møtet. Det kan være avlyst.',
  meeting_item_not_found: 'Hypotesen står ikke på agendaen lenger.',
  meeting_locked: 'Møtet er sendt til beslutningstakeren og kan ikke endres her.',
  meeting_not_started: 'Møtet er ikke startet. Start det fra agendaen.',
  meeting_not_awaiting_confirmation: 'Møtet venter ikke på bekreftelse lenger. Hent siden på nytt.',
  meeting_agenda_empty: 'Agendaen har ingen hypotese i Vurderes. Legg til én før du starter.',
  already_on_agenda: 'Hypotesen står alt på et møte som ikke er bekreftet.',
  not_in_review: 'Hypotesen står ikke i Vurderes lenger.',
  registration_incomplete: 'En rad mangler det utfallet krever — eier og svar ved Velg, dato og svar ved Parker.',
  in_pending_meeting: 'Hypotesen er til avgjørelse nå. Du kan endre den når avgjørelsen er tatt.',
  hypothesis_scores_disabled: 'Menneskesatte tall er skrudd av. Tallene ble ikke lagret.',
  reason_not_pending: 'Begrunnelsen er alt skrevet.',
  decider_not_on_list: 'Personen står ikke på beslutningstakerlista.',
  score_value_invalid: 'Tallet er utenfor skalaen.',
  quote_not_evidence: 'Sitatet er ikke en sak i grunnlaget.',
  score_sort_disabled: 'Menneskesatte tall er skrudd av, så registeret kan ikke sorteres på dem.',
  not_decider: 'Bare beslutningstakeren kan gjøre dette.',
  decider_is_submitter: 'Beslutningstakeren sendte inn en av hypotesene og kan ikke avgjøre den.',
  owner_is_decider: 'Eieren av testen kan ikke være beslutningstakeren.',
  revisit_date_out_of_range: 'Datoen må være fra i morgen og høyst tolv måneder fram.',
  related_hypothesis_required: 'Velg hypotesen den er testet i.',
  // H2.8: «Legg inn fra tidligere kanal». Skjemaet viser låsene FØR kallet; disse er for kappløpet.
  import_date_invalid: 'En dato er i framtiden eller går bakover. Sjekk datoene i skjemaet og historikken.',
  import_submitter_deactivated: 'Innsenderen er deaktivert hos dere. Gi personen tilgang igjen under Organisasjon, eller skriv en annen adresse.',
  own_hypothesis: 'Du kan ikke behandle en hypotese du selv har sendt inn.',
  admin_user_not_found: 'Fant ikke personen. Hent siden på nytt og velg igjen.',
};

/**
 * H2.8 (DD-80): kodene «Legg inn» kan få, i importens ord. Der den felles setningen snakker om noe
 * annet (møtet, innsendingen), sier denne det som gjelder her. Aldri HVOR adressen er i bruk (K2).
 */
const IMPORT_TEKSTER: Record<string, string> = {
  admin_user_email_taken: 'Adressen er i bruk hos en annen organisasjon. Skriv adressen innsenderen har hos dere.',
  decider_is_submitter: 'Beslutningstakeren er innsenderen og kan ikke ha valgt sin egen hypotese.',
  own_hypothesis: 'Vurdereren er innsenderen og kan ikke ha vurdert sin egen hypotese.',
  validation_failed: 'Vurdereren må være behandler i rommet eller eier, og eieren av testen må ha tilgang til rommet.',
  admin_user_not_found: 'Fant ikke en av personene i historikken. Hent siden på nytt og velg igjen.',
  hypothesis_incomplete: 'Hypotesen eller et steg i historikken mangler noe.',
};

/** Setningen ved «Legg inn»: importens egen der den finnes, ellers den felles. */
export function importBrukertekst(code: string, status: number): string {
  return IMPORT_TEKSTER[code] ?? brukertekstFor(code, status);
}

/** Ukjent kode: statusen sier det viktigste. `*_not_found` går alle til samme setning. */
export function brukertekstFor(code: string, status: number): string {
  const kjent = TEKSTER[code];
  if (kjent) return kjent;
  if (code.endsWith('_not_found') || status === 404) return 'Fant det ikke. Det kan være slettet, eller endret av noen andre.';
  if (status === 401) return TEKSTER['unauthenticated']!;
  if (status === 403) return TEKSTER['insufficient_role']!;
  if (status === 409) return 'Noen andre endret dette samtidig. Last siden på nytt og prøv igjen.';
  if (status === 429) return TEKSTER['rate_limited']!;
  if (status >= 500) return 'Tjenesten svarte med en feil. Prøv igjen om litt.';
  return 'Noe gikk galt. Prøv igjen.';
}
